// 試遊の記録を作者へ送る (配布版・同意した人だけ・2026-10-31)。
//
// なぜ要るか (作者 10-31): 「Bの方がバグの改善とかにも気づきやすいし、プレイヤーの行動もボスの行動も記録できるから、
//   コンテキストとしてとってゲーム制作へ応用できればとも思っている」。
//   = 開発版の検収パックで取っている細かい記録 (src/playlog_recorder.ts・1tick ごとの動き・入力・受け・被弾・死因) を、
//     友達の試遊でも取って受け口 (functions/api/playlog.ts) へ送る。 足あと (src/playtest_record.ts) の戦績も添える。
//
// 約束:
//   - 初めて開いた時に一言出して聞く。 「送らない」を選んだ人・答えていない人からは何も送らない
//     (その人は今まで通り、 足あとを写すボタンで渡せる)。 答えは localStorage に残す。
//   - 名前・端末の情報・場所は送らない。 送るのは記録の中身・版 (コミット)・端末ごとのでたらめな番号だけ。
//   - 見るだけ。 GameState を書き換えない。 送れなくても遊びは止めない。
//   - 開発版 (vite dev)・手元のサーバー (localhost)・他のページへの埋め込みでは送らない。
//   量: 1分あたり生で約 90KB → gzip で約 10KB。 20 秒ごとにまとめて送り、 ページを閉じる時は sendBeacon で残りを送る。
//   集計 = scripts/playtest_remote.mjs。 説明 = docs/research/2026-10-31-playtest-upload.md。

import type { GameState } from './types'
import type { Input } from './input'
import { PlaylogRecorder, type PlaylogChunk } from './playlog_recorder'
import { addRecvSink } from './playlog_hook'
import { currentRecord, type PlaytestRecord } from './playtest_record'

export const UPLOAD_ENDPOINT = '/api/playlog'
export const CONSENT_KEY = 'shinige.playtest.consent'   // 'yes' | 'no' (無い = まだ聞いていない)
export const CLIENT_KEY = 'shinige.playtest.client'     // 端末ごとのでたらめな番号
export const UPLOAD_FLUSH_MS = 20_000
// sendBeacon は 64KB 程度が上限。 20 秒分は生で約 30KB なので収まる。
export const BEACON_MAX_BYTES = 60_000

export type Consent = 'yes' | 'no' | null
export type UploadBody = PlaylogChunk & { kind: 'chunk'; client: string; build: string; footprint?: PlaytestRecord }

// 文言は shinige-world-japanese の UI の決まりで書いた (読点の間を使わない・舞台の言葉を足さない)。
export const CONSENT_TEXT = {
  line1: '遊んだ記録を作者に送ります。',
  line2: '名前などは送りません。',
  yes: '送る',
  no: '送らない',
} as const

export function readConsent(): Consent {
  try {
    const v = localStorage.getItem(CONSENT_KEY)
    return v === 'yes' || v === 'no' ? v : null
  } catch { return null }
}
export function writeConsent(v: 'yes' | 'no'): void {
  try { localStorage.setItem(CONSENT_KEY, v) } catch { /* 保存できなくても遊べる */ }
}

export function makeClientId(rand: () => number = Math.random): string {
  let s = ''
  for (let i = 0; i < 20; i += 1) s += '0123456789abcdefghijklmnopqrstuvwxyz'[Math.floor(rand() * 36)]
  return s
}
export function clientId(): string {
  try {
    const old = localStorage.getItem(CLIENT_KEY)
    if (old && /^[a-z0-9]{8,40}$/.test(old)) return old
    const id = makeClientId()
    localStorage.setItem(CLIENT_KEY, id)
    return id
  } catch { return makeClientId() }
}

// 送ってよい場所か (純関数・テスト対象)。 配布版のビルドで、 手元のサーバーでも埋め込みでもない時だけ。
export function uploadAllowedHere(o: { dev: boolean; hostname: string; embedded: boolean; search: string }): boolean {
  if (o.dev || o.embedded) return false
  if (/^(localhost|127\.|0\.0\.0\.0|\[::1\]|192\.168\.|10\.)/.test(o.hostname) || o.hostname === '') return false
  if (/[?&]advPreview(?:=|&|$)/.test(o.search)) return false
  return true
}

export function buildUploadBody(chunk: PlaylogChunk, client: string, build: string, footprint: PlaytestRecord | null): UploadBody {
  const body: UploadBody = { ...chunk, kind: 'chunk', client, build }
  // 区切り (戦いの終わり・ページを閉じる時) にだけ足あとを添える (毎回は重い・呼び手が渡す)
  if (footprint) body.footprint = footprint
  return body
}

async function gzipText(text: string): Promise<Blob | null> {
  if (typeof CompressionStream === 'undefined') return null
  try {
    const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'))
    return await new Response(stream).blob()
  } catch { return null }
}

async function postBody(body: UploadBody, beacon: boolean): Promise<boolean> {
  const text = JSON.stringify(body)
  if (beacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    // ページが閉じる時は圧縮を待てない。 生のまま送る (大きすぎれば諦める)。
    if (text.length > BEACON_MAX_BYTES) return false
    return navigator.sendBeacon(UPLOAD_ENDPOINT, new Blob([text], { type: 'application/json' }))
  }
  const gz = await gzipText(text)
  try {
    const r = await fetch(UPLOAD_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': gz ? 'application/gzip' : 'application/json' },
      body: gz ?? text,
      keepalive: beacon,
    })
    return r.ok
  } catch { return false }
}

let uploader: PlaylogRecorder | null = null

// 同意した後に1回だけ呼ぶ。 ここから先の戦闘を記録して送る。
export function startUpload(build: string): PlaylogRecorder | null {
  if (uploader) return uploader
  const client = clientId()
  uploader = new PlaylogRecorder({
    now: () => performance.now(),
    flushEveryMs: UPLOAD_FLUSH_MS,
    meta: () => ({
      buildId: build,
      upload: true,
      url: location.pathname + location.search,
    }),
    send: (chunk, beacon) => {
      // 戦いの終わりとページを閉じる時だけ、 その時点の足あとを添える
      const fp = chunk.final || beacon ? currentRecord(build) : null
      return postBody(buildUploadBody(chunk, client, build, fp), beacon)
    },
  })
  addRecvSink(uploader)
  window.addEventListener('pagehide', () => uploader?.flushOnHide())
  document.addEventListener('visibilitychange', () => { if (document.hidden) uploader?.flushOnHide() })
  return uploader
}

// main.ts の毎tick から呼ぶ。 同意していなければ何もしない。
export function playtestUploadTick(prev: GameState, next: GameState, input: Input): void {
  if (uploader) uploader.tick(prev, next, input)
}

// 初めて開いた時の一言。 答えると消え、 二度と出ない。
export function mountConsentPrompt(onAnswer: (v: 'yes' | 'no') => void): void {
  const box = document.createElement('div')
  box.id = 'playtest-consent'
  box.setAttribute('role', 'dialog')
  box.style.cssText = 'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:9999;'
    + 'max-width:calc(100vw - 32px);padding:14px 20px;display:flex;flex-wrap:wrap;align-items:center;gap:12px 18px;'
    + 'background:rgba(20,16,28,0.94);color:#e4e2ea;border:1px solid rgba(255,255,255,0.28);border-radius:4px;'
    + 'font:500 15px/1.6 "Yu Gothic UI","Hiragino Kaku Gothic ProN","Noto Sans JP",sans-serif'
  const text = document.createElement('div')
  text.textContent = `${CONSENT_TEXT.line1}${CONSENT_TEXT.line2}`
  const btns = document.createElement('div')
  btns.style.cssText = 'display:flex;gap:10px;margin-left:auto'
  const make = (label: string, v: 'yes' | 'no', strong: boolean): HTMLButtonElement => {
    const b = document.createElement('button')
    b.type = 'button'
    b.textContent = label
    b.style.cssText = 'padding:6px 16px;border-radius:4px;cursor:pointer;font:inherit;font-weight:600;'
      + (strong ? 'background:#e4e2ea;color:#14101c;border:1px solid #e4e2ea'
        : 'background:transparent;color:#e4e2ea;border:1px solid rgba(255,255,255,0.4)')
    b.addEventListener('click', () => { box.remove(); onAnswer(v) })
    return b
  }
  btns.append(make(CONSENT_TEXT.yes, 'yes', true), make(CONSENT_TEXT.no, 'no', false))
  box.append(text, btns)
  // ゲームのキー操作へ届かせない (クリックで答える)
  for (const type of ['mousedown', 'mouseup', 'keydown', 'keyup']) box.addEventListener(type, (e) => e.stopPropagation())
  document.body.appendChild(box)
}

// main.ts から1回だけ呼ぶ入口。
export function installPlaytestUpload(build: string, dev: boolean): void {
  if (typeof window === 'undefined') return
  let embedded = false
  try { embedded = window.top !== window } catch { embedded = true }
  if (!uploadAllowedHere({ dev, hostname: location.hostname, embedded, search: location.search })) return
  const consent = readConsent()
  if (consent === 'yes') { startUpload(build); return }
  if (consent === 'no') return
  mountConsentPrompt((v) => {
    writeConsent(v)
    if (v === 'yes') startUpload(build)
  })
}
